// System -> Cards — Owner-only (GL-38; owner grant 2026-10-11, spec §5 round 14).
// The shared-card list lives in D1. The owner adds a card by its last 4 digits
// and ticks each intercompany-split company that uses it. Every change is
// logged server-side; the most recent entries are shown here.
// Everything API-derived is rendered through h()/textContent (dom.ts).

import type { AdminClient } from "../api.js";
import { clear, h } from "../dom.js";
import type {
  CompanySlug,
  IcSplitCardChange,
  IcSplitCardsResponse,
  StaffRole,
} from "../types.js";
import { card, disclosure, emptyState, pageHeader } from "../ui/components.js";
import { timeAgoText } from "../ui/display-words.js";
import * as face from "../ui/face.js";
import { readerRole } from "../ui/facts.js";
import { failureCopy } from "../ui/failure.js";
import { createFreshnessCaption } from "../ui/freshness.js";
import { navLabel } from "../ui/sentences.js";
import { runWrite } from "../ui/write.js";

export interface CardsPageOptions {
  client: AdminClient;
  root: HTMLElement;
  isCurrent?: () => boolean;
  /** Who is reading; the shell passes it where it knows (optional). */
  role?: StaffRole;
}

const LAST4 = /^[0-9]{4}$/;

export async function renderCardsPage(opts: CardsPageOptions): Promise<void> {
  const { client, root } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  const role = readerRole(opts.role);
  clear(root);
  const page = h("div", { class: "settings-stack" });
  root.append(page);

  page.append(
    pageHeader(
      navLabel("cards"),
      "Tick each company that uses a shared card, then save. To remove a card, use Remove card.",
    ),
  );

  const freshnessHost = h("div", { "data-role": "freshness-host" });
  page.append(freshnessHost);

  const banner = h("div", {
    class: "banner banner-err",
    role: "alert",
    "data-role": "cards-error",
    hidden: "true",
  });
  page.append(banner);

  const content = h("div", { "data-role": "cards-content" });
  page.append(content);
  content.append(h("p", { class: "muted" }, ["Loading…"]));

  let data: IcSplitCardsResponse = { splitCompanies: [], cards: [], changes: [] };
  let directory: face.CompanyDirectory = face.createCompanyDirectory([], { complete: false });
  let loadedAt = new Date();
  /**
   * Reads and writes both take a generation. A read that started before a
   * later save or Remove is stale and is dropped, so a held Refresh cannot
   * repaint the state the write just replaced (rd round 11 review, MEDIUM 4).
   */
  let generation = 0;

  const label = (slug: CompanySlug): string => face.company(slug, directory, "sentence");

  const paintFreshness = (): void => {
    clear(freshnessHost);
    freshnessHost.append(
      createFreshnessCaption({
        updatedAt: loadedAt,
        onRefresh: () => {
          void reload();
        },
      }),
    );
  };

  const hideBanner = (): void => {
    clear(banner);
    banner.hidden = true;
    banner.setAttribute("hidden", "true");
  };

  const showError = (sentence: string, detail?: string): void => {
    clear(banner);
    banner.hidden = false;
    banner.removeAttribute("hidden");
    banner.append(h("p", {}, [sentence]));
    if (detail !== undefined && detail !== "") {
      banner.append(disclosure("Show details", h("p", {}, [detail])));
    }
  };

  const reload = async (): Promise<void> => {
    const mine = ++generation;
    hideBanner();
    try {
      const [cardsRes, companiesRes] = await Promise.all([
        client.getIcSplitCards(),
        client.listCompanies().catch(() => null),
      ]);
      if (!stillCurrent() || mine !== generation) return;
      data = cardsRes;
      if (companiesRes !== null) {
        directory = face.createCompanyDirectory(
          companiesRes.companies.map((row) => ({
            slug: row.slug,
            displayName: row.displayName,
          })),
          { complete: true },
        );
      }
      loadedAt = new Date();
      paintFreshness();
      paint();
    } catch (error) {
      if (!stillCurrent() || mine !== generation) return;
      clear(content);
      const copy = failureCopy(error, { role, context: "read" });
      showError("We could not load the cards. Try again.", copy.detail ?? undefined);
      const retry = h(
        "button",
        {
          type: "button",
          class: "btn btn-primary",
          "data-action": "retry",
          "aria-label": "Retry loading cards",
        },
        ["Try again"],
      );
      retry.addEventListener("click", () => {
        void reload();
      });
      content.append(retry);
    }
  };

  /**
   * One write home: a save and a Remove both go through `runWrite`, and the
   * re-read lives in `afterLanded` so a landed save can never be reported as
   * "did not save" when only the refresh failed (rc-2 sister 2).
   */
  const putCard = async (
    cardLast4: string,
    companies: string[],
    scope: HTMLElement,
    button: HTMLButtonElement,
  ): Promise<void> => {
    hideBanner();
    button.disabled = true;
    // Any read already in flight predates this write; drop it when it lands.
    generation += 1;
    const outcome = await runWrite({
      key: `card-save:${cardLast4}`,
      scope,
      gate: () => null,
      send: () => client.putIcSplitCard({ cardLast4, companies }),
      afterLanded: async () => {
        await reload();
      },
      stillCurrent,
    });
    if (outcome.kind === "landed") {
      if (outcome.afterLandedFailed === true) {
        showError("Saved, but the list could not be refreshed. Press Refresh to see it.");
      }
      return;
    }
    if (!stillCurrent()) return;
    button.disabled = false;
    const copy = failureCopy(outcome.error, { role, context: "write" });
    showError(copy.sentence, copy.detail ?? undefined);
  };

  const save = async (
    cardLast4: string,
    companies: string[],
    button: HTMLButtonElement,
  ): Promise<void> => {
    await putCard(cardLast4, companies, button.closest("li") ?? page, button);
  };

  /**
   * Removing a card is its own deliberate act (R8-18). The save path used to be
   * the delete path: clearing every tick and saving sent `companies: []`, and
   * the Worker deleted the card.
   */
  const removeCard = async (
    cardLast4: string,
    button: HTMLButtonElement,
  ): Promise<void> => {
    await putCard(cardLast4, [], button.closest("li") ?? page, button);
  };

  const tickBoxes = (
    cardKey: string,
    selected: ReadonlySet<CompanySlug>,
  ): { wrap: HTMLElement; chosen: () => CompanySlug[]; inputs: HTMLInputElement[] } => {
    const wrap = h("div", { class: "card-ticks", role: "group" });
    const boxes: Array<{ slug: CompanySlug; input: HTMLInputElement }> = [];
    for (const slug of data.splitCompanies as CompanySlug[]) {
      const shown = label(slug);
      const input = h("input", {
        type: "checkbox",
        "data-card": cardKey,
        "data-company": slug,
        "aria-label": `${shown} uses card ${cardKey}`,
      });
      input.checked = selected.has(slug);
      boxes.push({ slug, input });
      wrap.append(h("label", { class: "card-tick" }, [input, ` ${shown}`]));
    }
    return {
      wrap,
      chosen: () => boxes.filter((b) => b.input.checked).map((b) => b.slug),
      inputs: boxes.map((b) => b.input),
    };
  };

  const paintCard = (card: { cardLast4: string; companies: string[] }): HTMLLIElement => {
    const ticks = tickBoxes(card.cardLast4, new Set(card.companies as CompanySlug[]));
    const saveBtn = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "card-save",
        "data-card": card.cardLast4,
        "aria-label": `Save card ${card.cardLast4}`,
      },
      ["Save"],
    );
    // A card belongs to at least one company: with no tick there is nothing to
    // save, and the save would delete the card.
    const syncSave = (): void => {
      saveBtn.disabled = ticks.chosen().length === 0;
    };
    for (const input of ticks.inputs) input.addEventListener("change", syncSave);
    syncSave();
    saveBtn.addEventListener("click", () => {
      if (saveBtn.disabled) return;
      void save(card.cardLast4, ticks.chosen(), saveBtn);
    });

    const removeBtn = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "card-remove",
        "data-card": card.cardLast4,
        "aria-label": `Remove card ${card.cardLast4}`,
      },
      ["Remove card"],
    );
    const confirmHost = h("div", {
      "data-role": "card-remove-confirm",
      hidden: "true",
    });
    removeBtn.addEventListener("click", () => {
      clear(confirmHost);
      confirmHost.hidden = false;
      confirmHost.removeAttribute("hidden");
      const confirm = h(
        "button",
        {
          type: "button",
          class: "btn btn-primary",
          "data-action": "confirm-card-remove",
          "data-card": card.cardLast4,
        },
        ["Remove"],
      );
      const cancel = h(
        "button",
        {
          type: "button",
          class: "btn btn-ghost",
          "data-action": "cancel-card-remove",
        },
        ["Keep it"],
      );
      cancel.addEventListener("click", () => {
        clear(confirmHost);
        confirmHost.hidden = true;
        confirmHost.setAttribute("hidden", "true");
      });
      confirm.addEventListener("click", () => {
        void removeCard(card.cardLast4, confirm);
      });
      confirmHost.append(
        h("p", { class: "muted" }, [
          `Remove card ending ${card.cardLast4}? Receipts will no longer attach to it.`,
        ]),
        h("div", { class: "form-row" }, [confirm, cancel]),
      );
    });

    return h("li", { class: "card-row", "data-role": "card-row" }, [
      h("strong", {}, [`Card ending ${card.cardLast4}`]),
      ticks.wrap,
      saveBtn,
      removeBtn,
      confirmHost,
    ]);
  };

  const paintAdd = (): HTMLElement => {
    const input = h("input", {
      type: "text",
      name: "new-card-last4",
      "data-role": "new-card-last4",
      "aria-label": "New card, last 4 digits",
      placeholder: "Last 4 digits",
      autocomplete: "off",
    });
    input.maxLength = 4;
    input.inputMode = "numeric";
    const ticks = tickBoxes("new", new Set());
    const addBtn = h(
      "button",
      {
        type: "button",
        class: "btn btn-secondary",
        "data-action": "card-add",
        "aria-label": "Add card",
      },
      ["Add card"],
    );
    addBtn.addEventListener("click", () => {
      const last4 = input.value.trim();
      const companies = ticks.chosen();
      if (!LAST4.test(last4)) {
        showError("Enter exactly four digits.");
        return;
      }
      if (companies.length === 0) {
        showError("Tick at least one company, then add the card.");
        return;
      }
      void save(last4, companies, addBtn);
    });
    return card(
      h("div", { class: "card-add", "data-role": "card-add" }, [
        h("h2", {}, ["Add a card"]),
        h("p", { class: "muted" }, [
          "Enter the last four digits, tick every company that uses it, then add the card.",
        ]),
        input,
        ticks.wrap,
        addBtn,
      ]),
    );
  };

  const changeLine = (c: IcSplitCardChange): HTMLLIElement => {
    const fmt = (slugs: string[]): string =>
      slugs.length === 0
        ? "none"
        : slugs.map((slug) => label(slug as CompanySlug)).join(", ");
    const when = formatWhen(c.changedAt);
    // Never the raw sign-in id on the face (round 12's R4 rule): an email is a
    // name a person knows; any other identity is this owner's own change, so
    // say "the owner". The raw value stays behind Show details via `changedAt`
    // only — the id itself adds nothing a reader can use.
    const actor = face.person(c.changedBy) ?? "the owner";
    return h("li", { "data-role": "card-change" }, [
      h("p", {}, [
        `${when} — ${actor}: card ending ${c.cardLast4}, ${fmt(c.companiesBefore)} → ${fmt(c.companiesAfter)}`,
      ]),
      disclosure("Show details", h("p", {}, [c.changedAt])),
    ]);
  };

  const formatWhen = (iso: string): string => {
    const phrase = timeAgoText(iso);
    return phrase === "" ? "an unknown time" : phrase;
  };

  const paint = (): void => {
    clear(content);
    if (data.cards.length === 0) {
      const empty = emptyState(
        "No cards yet. Add one below. Until you do, a receipt cannot attach to a shared card.",
      );
      empty.setAttribute("data-role", "cards-empty");
      content.append(empty);
    } else {
      const list = h("ul", { class: "cards-list", "data-role": "cards-list" });
      for (const card of data.cards) list.append(paintCard(card));
      content.append(list);
    }
    content.append(paintAdd());
    content.append(h("h2", {}, ["Recent changes"]));
    if (data.changes.length === 0) {
      content.append(
        h("p", { class: "muted" }, ["No changes yet. Each save will be listed here."]),
      );
    } else {
      const log = h("ul", { class: "cards-changes", "data-role": "cards-changes" });
      for (const c of data.changes) log.append(changeLine(c));
      content.append(log);
    }
  };

  await reload();
}
